import { faGift, faMicrophone } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  Checkbox,
  CloseIcon,
  CloseCircleIcon,
  FlexBox,
  JoonUIColor,
  SPACING,
  TextArea,
  Typography,
  Modal,
} from "@joonapp/web-shared"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useInventoryGiftShopModalStore } from "./hooks/useInventoryGiftShopModalStore"
import { useSendMailModalStore } from "./hooks/useSendMailModalStore"
import useSendMailMutation from "./hooks/useSendMailMutation"
import MailAudioButton from "./MailAudioButton"
import MailAudioModal, { useMailAudioModalStore } from "./MailAudioModal"
import MailPhotoUploadButton from "./MailboxPhotoUploadButton"
import {
  SmallImageModal,
  useSmallImageModalStore,
} from "../../../../components/modals/SmallImageModal"
import SlideUpModal from "../../../../components/slideUpModal/SlideUpModal"
import { GRADIENTS } from "../../../../constants"
import {
  useFamilyQuery,
  useInventoryGiftsQuery,
} from "../../../../networking/queries"
import { useParentShopQuery } from "../../../../networking/rewards/queries"
import { FamilyQuerySelect } from "../../../../types"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../../util/analytics"
import { GetChildStartedContent } from "../../../family/GetChildStartedModal"

const SendMailModal = () => {
  const {
    isOpen,
    onClose,
    selectedChildId,
    setSelectedChildId,
    message,
    setMessage,
    selectedGift,
    setSelectedGift,
    imagePreviewUrl,
    setImagePreviewUrl,
    imageFile,
    setImageFile,
    audioPreviewUrl,
    setAudioPreviewUrl,
    audioFile,
    setAudioFile,
  } = useSendMailModalStore()
  const { onOpen: onOpenImageModal } = useSmallImageModalStore()
  const { onOpen: openInventoryGiftShopModal } =
    useInventoryGiftShopModalStore()
  const { onOpen: openMailAudioModal } = useMailAudioModalStore()
  const { t } = useTranslation()

  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  // preload inventory and shop
  useInventoryGiftsQuery()
  useParentShopQuery()

  const sendMailMutation = useSendMailMutation()

  useEffect(() => {
    if (!isOpen) return
    if (!selectedChildId && children?.[0]?.user.id) {
      setSelectedChildId(children?.[0]?.user.id)
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSendMailClick = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.SEND_FAMILY_MAIL, {
      has_gift: !!selectedGift,
      has_audio: !!audioFile,
      has_image: !!imageFile,
    })
    sendMailMutation.mutate()
  }

  const hasOnboardedChildren = useMemo(() => {
    if (selectedChildId)
      return !!children?.find((child) => child.user.id === selectedChildId)
        ?.user.active_doter.date_hatched
    else
      return children?.some((child) => !!child.user.active_doter.date_hatched)
  }, [children, selectedChildId])

  if (!hasOnboardedChildren)
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="flex flex-col gap-4 mx-auto items-center p-6 overflow-y-auto h-full">
          <Typography variant="h3">{t("MAIL_ONBOARD_FIRST")}</Typography>
          <GetChildStartedContent childId={selectedChildId} />
        </div>
      </Modal>
    )

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      showCard={true}
      size="fit-content"
      backgroundType="overlay"
      zIndex={1001}
    >
      <button className="absolute right-4 top-4 p-2" onClick={onClose}>
        <CloseIcon color={JoonUIColor.icon.neutral} />
      </button>
      <FlexBox
        direction="column"
        gap={SPACING.space2}
        wrap={false}
        style={{
          padding: SPACING.space6,
          background: GRADIENTS.gradient100,
          height: "60vh",
        }}
      >
        <Typography variant="h3"> </Typography>

        <Typography variant="bodyBold">{t("MAIL_TO_LABEL")}</Typography>
        <FlexBox
          direction="row"
          gap={SPACING.space2}
          wrap={true}
          style={{ textWrap: "nowrap" }}
        >
          {children?.map(
            (child) =>
              child.user.active_doter.name && (
                <Checkbox
                  key={child.user.id}
                  name={"Select Child to Send Mail"}
                  label={child.user.name}
                  selected={selectedChildId === child.user.id}
                  onChange={() => setSelectedChildId(child.user.id)}
                  inputType="radio"
                  hideBorder
                  style={{
                    padding: `${SPACING.space1} ${SPACING.space2}`,
                    background: JoonUIColor.background.primaryNeutral,
                    border: `1px solid ${selectedChildId === child.user.id ? JoonUIColor.border.accent : JoonUIColor.border.default}`,
                    boxShadow: "4px 6px 6px 0px rgba(0, 0, 0, 0.08)",
                  }}
                />
              )
          )}
        </FlexBox>
        <Typography
          variant="bodyBold"
          style={{ textAlign: "center", marginTop: SPACING.space2 }}
        >
          {t("MAIL_MESSAGE")}
        </Typography>
        <TextArea
          name="Message"
          placeholder={t("MAIL_MESSAGE_PLACEHOLDER")}
          value={message}
          onChange={(e: any) => setMessage(e.target.value)}
          maxLength={150}
          showCounter={true}
          fullWidth
          style={{
            lineHeight: "1.25rem",
            borderRadius: SPACING.space2,
            border: `1px solid ${JoonUIColor.border.default}`,
            background: JoonUIColor.background.primaryNeutral,
          }}
        />
        <Typography variant="bodyBold">{t("MAIL_ADD_ATTACHMENT")}</Typography>
        <div className="flex flex-row gap-2 w-full">
          <Button
            text={
              <div className="flex flex-row gap-1 items-center">
                <FontAwesomeIcon icon={faGift} />
                <Typography variant="body">{t("MAIL_GIFT")}</Typography>
              </div>
            }
            onClick={openInventoryGiftShopModal}
            buttonType="secondary"
            size="small"
            disabled={!!selectedGift?.shop_item.name.length}
            fullWidth
          />
          <MailPhotoUploadButton />
          <Button
            text={
              <div className="flex flex-row gap-1 items-center">
                <FontAwesomeIcon icon={faMicrophone} />
                <Typography variant="body">{t("MAIL_AUDIO")}</Typography>
              </div>
            }
            onClick={openMailAudioModal}
            buttonType="secondary"
            size="small"
            disabled={!!audioPreviewUrl}
            fullWidth
          />
        </div>
        <div className="flex flex-row gap-2 w-full mt-2">
          {!!selectedGift?.shop_item.name.length && (
            <div className="flex flex-row w-[50px] h-[50px] items-center justify-center rounded-md border border-borderPrimary relative bg-backgroundPrimary p-1">
              <img
                className="flex-1"
                src={selectedGift?.shop_item.image_url}
                alt="Gift"
              />
              <button
                onClick={() => setSelectedGift(null)}
                className="absolute -top-3 -right-3 p-1!"
              >
                <CloseCircleIcon size={16} color={JoonUIColor.icon.neutral} />
              </button>
            </div>
          )}
          {!!imagePreviewUrl && (
            <button
              onClick={() => {
                onOpenImageModal(imagePreviewUrl)
              }}
              className="relative"
            >
              <img
                className="w-[50px] h-[50px] rounded-md border border-borderPrimary object-cover"
                src={imagePreviewUrl}
                alt={imagePreviewUrl}
              />
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setImagePreviewUrl(null)
                  setImageFile(null)
                }}
                className="absolute -top-3 -right-3 p-1!"
              >
                <CloseCircleIcon size={16} color={JoonUIColor.icon.neutral} />
              </button>
            </button>
          )}
          {!!audioPreviewUrl && (
            <div className="relative">
              <MailAudioButton audioUrl={audioPreviewUrl} />
              <button
                onClick={() => {
                  setAudioPreviewUrl(null)
                  setAudioFile(null)
                }}
                className="absolute -top-3 -right-3 p-1!"
              >
                <CloseCircleIcon size={16} color={JoonUIColor.icon.neutral} />
              </button>
            </div>
          )}
        </div>
      </FlexBox>
      <FlexBox
        direction="row"
        justify="center"
        style={{
          background:
            "linear-gradient(rgba(255, 255, 255, 0.8) 2.83%, rgba(255, 255, 255, 0) 51.53%, rgba(255, 255, 255, 0.8) 99.81%), rgb(233, 234, 250)",
          padding: SPACING.space6,
        }}
      >
        <Button
          isLoading={sendMailMutation.isLoading}
          text={t("MAIL_SEND")}
          fullWidth
          color={JoonUIColor.semantic.primary}
          onClick={onSendMailClick}
          disabled={!selectedChildId || !message}
        />
      </FlexBox>
      <MailAudioModal />
      <SmallImageModal />
    </SlideUpModal>
  )
}

export default SendMailModal
